<template>
  <VDialogV3
    id="driver-card"
    position="right"
    width="600px"
    height="100vh"
    :entity="localDriver"
    :context-menu="DriversService.contextMenu"
    :tabs="tabs"
    v-model:active-tab="activeTab"
    :hide-footer="activeTab?.id === 'feedbacks'"
    :submit-method="submit"
    @close="onClose"
  >
    <template #badges>
      <VBadge
        v-if="localDriver.isFavorite"
      >
        В избранном
      </VBadge>
    </template>

    <!-- ОТЗЫВЫ -->

    <template v-if="activeTab?.id === 'feedbacks'">
      <section v-if="$core.utils.isEmptyArray(feedbacks)">
        Отзывов нет
      </section>

      <!-- Каждый отзыв — отдельный блок, между блоками разделитель диалога -->
      <template
        v-for="(feedback, index) in feedbacks"
        :key="feedback.id"
      >
        <hr v-if="index > 0">

        <section class="driver-feedback">
          <header>{{ feedback.owner?.name ?? '--' }}</header>

          <div class="stars">
            <VIcon
              v-for="star in 5"
              :key="star"
              :material="DriversService.getRateStarIcon(feedback.rate, star)"
            />
          </div>

          <p v-if="$core.utils.isNullOrUndefined(feedback.comment) === false">
            {{ feedback.comment }}
          </p>
        </section>
      </template>
    </template>

    <!-- ОСНОВНОЕ -->

    <template v-else>
      <DriverForm :driver="localDriver"/>

      <template v-if="localDriver.isExisting">
        <hr>

        <section id="driver-rating">
          <header>Рейтинг</header>

          <dl>
            <dt>Средняя оценка:</dt>
            <dd class="average-rate">
              <span
                v-if="$core.utils.isNullOrUndefined(localDriver.averageRate) === false"
                class="stars"
              >
                <VIcon
                  v-for="star in 5"
                  :key="star"
                  :material="DriversService.getRateStarIcon(localDriver.averageRate, star)"
                />
              </span>
              {{ localDriver.averageRateString }}
            </dd>

            <dt>Последний отзыв:</dt>
            <dd :title="lastComment">{{ lastComment ?? '--' }}</dd>
          </dl>
        </section>

        <hr>

        <DriverUserFeedback
          :driver="localDriver"
          :feedback="localFeedback"
        />
      </template>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./DriverCard.scss"/>

<script lang="ts">

import Driver                 from '@/domains/Drivers/Driver';
import DriversService         from '@/domains/Drivers/DriversService';
import DriverFeedback         from '@/domains/Drivers/DriverFeedback';
import DriverFeedbacksService from '@/domains/Drivers/DriverFeedbacksService';

import DriverForm             from '@/domains/Drivers/components/DriverCard/DriverForm.vue';
import DriverUserFeedback     from '@/domains/Drivers/components/DriverCard/DriverUserFeedback.vue';

import type { DialogTab }     from '@/lib/components/VDialogV3/VDialogV3.vue';

export default defineComponent({
  name: 'DriverCard',

  components: {
    DriverForm,
    DriverUserFeedback,
  },

  props: {
    /** ID водителя — карточка сама загружает объект */
    id: {
      type:    Number,
      default: null,
    },

    /** Водитель, по которому открыта карточка. Карточка перечитывает его по id */
    driver: {
      type:    Driver as PropType<Driver | null>,
      default: null,
    },
  },

  setup() {
    return {
      DriversService,
    };
  },

  data() {
    return {
      localDriver: undefined as unknown as Driver,

      /** Водитель в том виде, в каком пришёл с бэка — с ним сравниваем правки перед сохранением */
      initialDriver: null as Driver | null,

      /** Своя оценка — отдельная сущность, уходит своим запросом по общей кнопке «Сохранить» */
      localFeedback: undefined as unknown as DriverFeedback,

      /** Все отзывы о водителе, свежие сверху */
      feedbacks: [] as DriverFeedback[],

      activeTab: null as DialogTab | null,

      /** Водитель сохранён, а оценка нет — при закрытии хост всё равно должен обновиться */
      isChanged: false,
    };
  },

  async mounted() {
    await this.init();
  },

  computed: {
    /**
     * Вкладка «Отзывы» — только история, оценку ставят на «Основном».
     * Табы есть только у существующего водителя — у нового отзывов быть не может
     */
    tabs(): DialogTab[] | null {
      if (this.localDriver?.isExisting === true) {
        return [
          { id: 'main',      name: 'Основное' },
          { id: 'feedbacks', name: 'Отзывы', badge: this.localDriver.feedbackCount },
        ];
      }
      else {
        return null;
      }
    },

    /** Текст последнего отзыва с комментарием; null — таких отзывов нет */
    lastComment(): string | null {
      const feedback = this.feedbacks.find(
        _feedback => this.$core.utils.isNullOrUndefined(_feedback.comment) === false
      );

      if (feedback === undefined) {
        return null;
      }
      else {
        return feedback.comment;
      }
    },

    /** Изменены ли поля водителя. Новый водитель всегда считается изменённым */
    isDriverChanged(): boolean {
      if (this.localDriver.isExisting === false) {
        return true;
      }
      else {
        return this.localDriver.name !== this.initialDriver.name
          || this.localDriver.registrationNumber !== this.initialDriver.registrationNumber
          || this.localDriver.vehicle?.id !== this.initialDriver.vehicle?.id
          || this.localDriver.vehicleOwnershipType?.id !== this.initialDriver.vehicleOwnershipType?.id
          || this.localDriver.isFavorite !== this.initialDriver.isFavorite
          || this.localDriver.phone !== this.initialDriver.phone
          || this.localDriver.inn !== this.initialDriver.inn
          || this.localDriver.licenseNumber !== this.initialDriver.licenseNumber;
      }
    },

    /**
     * Изменена ли своя оценка. Без выбранных звёзд сохранять нечего — бэк оценку обязательной требует.
     * Оценки ещё не было — любая выбранная звезда уже изменение
     */
    isFeedbackChanged(): boolean {
      if (this.$core.utils.isNullOrUndefined(this.localFeedback?.rate) === true) {
        return false;
      }
      else if (this.$core.utils.isNullOrUndefined(this.localDriver.feedback) === true) {
        return true;
      }
      else {
        return this.localFeedback.rate !== this.localDriver.feedback.rate
          || this.localFeedback.comment !== this.localDriver.feedback.comment;
      }
    },
  },

  methods: {
    async init(): Promise<void> {
      const driverId = this.driver?.id ?? this.id;

      if (this.$core.utils.isNullOrUndefined(driverId) === false) {
        this.initialDriver = await DriversService.get(driverId);
        this.localDriver   = this.$core.utils.cloneDeep(this.initialDriver);
      }
      else {
        this.localDriver = new Driver();
      }

      // Синхронно сразу за водителем, до первого await: на await карточка уже рендерится
      // и обращается к localFeedback
      this.initFeedback();

      if (this.localDriver.isExisting === true) {
        await this.loadFeedbacks();
      }
    },

    /** Своя оценка: копия уже поставленной либо новая */
    initFeedback(): void {
      if (this.$core.utils.isNullOrUndefined(this.localDriver.feedback) === false) {
        this.localFeedback = this.$core.utils.cloneDeepWithReference(this.localDriver.feedback, ['driver', 'owner']);
      }
      else {
        this.localFeedback = DriverFeedback.fromDriver(this.localDriver);
      }
    },

    /**
     * Все отзывы о водителе. Бэк порядок не гарантирует и дат не отдаёт — сортируем по id, новые сверху.
     * Отредактированный отзыв остаётся на месте первой оценки.
     * Оценок нет — список не запрашиваем
     */
    async loadFeedbacks(): Promise<void> {
      if (this.localDriver.feedbackCount === 0) {
        this.feedbacks = [];
      }
      else {
        const feedbacks = await DriverFeedbacksService.getList({ driverId: this.localDriver.id });

        this.feedbacks = feedbacks.sort((a, b) => b.id - a.id);
      }
    },

    /**
     * Сохраняет водителя и свою оценку одной кнопкой — каждый своим запросом и только если изменён.
     * Добавление в избранное уходит вместе с водителем (isFavorite: true).
     * Водитель сохранён, а оценка нет — карточка остаётся открытой; водителя запоминаем сохранённым,
     * чтобы повторное «Сохранить» не отправило его второй раз со старой версией
     */
    async submit(): Promise<boolean> {
      let isSaved = true;

      if (this.isDriverChanged === true) {
        isSaved = await DriversService.upsert(this.localDriver) !== null;

        if (isSaved === true) {
          this.initialDriver = this.$core.utils.cloneDeep(this.localDriver);
          this.isChanged     = true;
        }
      }

      if (isSaved === true && this.isFeedbackChanged === true) {
        isSaved = await DriverFeedbacksService.upsert(this.localFeedback);
      }

      return isSaved;
    },

    onClose(): void {
      if (this.isChanged === true) {
        this.$core.dialogs.DriverCard.resolve();
      }
      else {
        // ничего не сохраняли — диалог закроет сам VDialogV3
      }
    },
  },
});
</script>
